import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTenant } from './TenantContext.jsx';
import { getSession } from '../lib/api.js';

/**
 * EditModeContext — Storefront-First-Editing (Issue #2, WordPress-Prinzip).
 *
 * Ein eingeloggter tenant_admin (Demo-/Interessenten-Login) bleibt in der
 * Storefront: er sieht den kompletten Shop, und alle editierbaren Elemente
 * werden inline bedienbar, wenn der Bearbeiten-Modus aktiv ist.
 *
 * canEdit  — Session-Rolle tenant_admin für DIESEN Mandanten (serververifiziert).
 * editMode — Schalter: inline-Elemente zeigen Bearbeitungs-Affordanzen.
 * revision — Zähler: nach jedem Inline-Speichern erhöht, damit Content-Hooks
 *            (useContentBlocks) neu laden und die Änderung sofort sichtbar ist.
 * designOpen / designSection — Design-Drawer (Issue #3 Paket B): Farben,
 *            Schriften, Hintergrundbild. Unabhängig vom Bearbeiten-Modus; die
 *            Section ('colors' | 'fonts' | 'background') erlaubt Deep-Links aus
 *            der Storefront (z. B. „Hintergrund ändern“ am Hero).
 */
const EditModeContext = createContext(null);

export function EditModeProvider({ children }) {
  // useTenant try/catch: isoliertes Rendering (Tests) ohne TenantProvider.
  let tenant = null;
  try {
    tenant = useTenant()?.tenant || null;
  } catch {
    tenant = null;
  }
  const [session, setSession] = useState({ role: null, tenantId: null });
  const [sessionExpired, setSessionExpired] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [revision, setRevision] = useState(0);
  const [sessionCheckKey, setSessionCheckKey] = useState(0);
  const [design, setDesign] = useState({ open: false, section: null });
  const [productDrafts, setProductDrafts] = useState({});
  const [unsavedEdits, setUnsavedEdits] = useState({});

  useEffect(() => {
    let isMounted = true;
    getSession()
      .then((s) => {
        if (isMounted) {
          if (s?.authenticated) {
            setSession({ role: s?.role || null, tenantId: s?.tenantId || null });
            setSessionExpired(false);
          } else {
            if (session.role) {
              setSessionExpired(true);
            }
            setSession({ role: null, tenantId: null });
          }
        }
      })
      .catch(() => {
        /* anonyme Besucher: kein Edit-Modus */
      });
    return () => {
      isMounted = false;
    };
  }, [sessionCheckKey]);

  const canEdit = useMemo(
    () => Boolean(session.role === 'tenant_admin' && session.tenantId && tenant?.id && session.tenantId === tenant.id),
    [session, tenant?.id],
  );

  const hasUnsavedEdits = useMemo(() => {
    return Object.values(unsavedEdits).some(Boolean) ||
      Object.values(productDrafts).some((d) => d && Boolean(d.isOpen && d.value));
  }, [unsavedEdits, productDrafts]);

  // Window beforeunload warning when uncommitted edits exist
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleBeforeUnload = (e) => {
      if (hasUnsavedEdits) {
        e.preventDefault();
        e.returnValue = 'Sie haben ungespeicherte Änderungen vorliegen.';
        return 'Sie haben ungespeicherte Änderungen vorliegen.';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedEdits]);

  // Ohne Bearbeitungsrecht (Abmeldung, Mandantenwechsel) bleibt nichts offen.
  useEffect(() => {
    if (!canEdit) setDesign((d) => (d.open ? { open: false, section: null } : d));
  }, [canEdit]);

  const toggleEditMode = useCallback(() => setEditMode((v) => !v), []);
  const exitEditMode = useCallback(() => setEditMode(false), []);
  const enterEditMode = useCallback(() => setEditMode(true), []);
  const bumpRevision = useCallback(() => setRevision((v) => v + 1), []);
  const openDesign = useCallback((section = null) => setDesign({ open: true, section }), []);
  const closeDesign = useCallback(() => setDesign({ open: false, section: null }), []);
  const toggleDesign = useCallback(() => setDesign((d) => ({ open: !d.open, section: null })), []);
  // Nach Login (z. B. via /admin-Gate) Session neu lesen, damit canEdit greift.
  const recheckSession = useCallback(() => setSessionCheckKey((k) => k + 1), []);
  const notifySessionExpired = useCallback((expired = true) => setSessionExpired(Boolean(expired)), []);

  const setUnsavedEdit = useCallback((key, isDirty) => {
    setUnsavedEdits((prev) => {
      if (Boolean(prev[key]) === Boolean(isDirty)) return prev;
      return {
        ...prev,
        [key]: Boolean(isDirty),
      };
    });
  }, []);

  // Entwurfs-Persistenz für Produkt-Inline-Editor (überlebt SPA-Navigation)
  const setProductDraft = useCallback((key, draftData) => {
    setProductDrafts((prev) => ({
      ...prev,
      [key]: draftData,
    }));
  }, []);

  const clearProductDraft = useCallback((key) => {
    setProductDrafts((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      canEdit,
      editMode,
      toggleEditMode,
      exitEditMode,
      enterEditMode,
      revision,
      bumpRevision,
      recheckSession,
      sessionExpired,
      notifySessionExpired,
      hasUnsavedEdits,
      setUnsavedEdit,
      designOpen: design.open,
      designSection: design.section,
      openDesign,
      closeDesign,
      toggleDesign,
      productDrafts,
      setProductDraft,
      clearProductDraft,
    }),
    [
      canEdit,
      editMode,
      toggleEditMode,
      exitEditMode,
      enterEditMode,
      revision,
      bumpRevision,
      recheckSession,
      sessionExpired,
      notifySessionExpired,
      hasUnsavedEdits,
      setUnsavedEdit,
      design,
      openDesign,
      closeDesign,
      toggleDesign,
      productDrafts,
      setProductDraft,
      clearProductDraft,
    ],
  );

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}

export function useEditMode() {
  const ctx = useContext(EditModeContext);
  if (!ctx) {
    return {
      canEdit: false,
      editMode: false,
      toggleEditMode: () => {},
      exitEditMode: () => {},
      enterEditMode: () => {},
      revision: 0,
      bumpRevision: () => {},
      recheckSession: () => {},
      sessionExpired: false,
      notifySessionExpired: () => {},
      hasUnsavedEdits: false,
      setUnsavedEdit: () => {},
      designOpen: false,
      designSection: null,
      openDesign: () => {},
      closeDesign: () => {},
      toggleDesign: () => {},
      productDrafts: {},
      setProductDraft: () => {},
      clearProductDraft: () => {},
    };
  }
  return ctx;
}